import MainTopHeader from "./main.top.header";
import "jsvectormap/dist/jsvectormap.min.css";
import { SIDEBAR_COOKIE_NAME, SidebarProvider } from "@/components/ui/sidebar";
import AppSidebar from "./app.sidebar";
import { PropsWithChildren } from "react";
import getCookie from "@/utils/cookies";
import { useLocation } from "react-router-dom";
import { PATH_CALL_CENTER_CALL_LEAD, PATH_CALL_CENTER_JOIN } from "@/routes/paths";
import { useCallCenters } from "@/hooks/call-center/useCallCenters";

type Props = {
  children: React.ReactNode;
};

// Agent in-call routes (video session + voice call). join-meeting-as-admin
// keeps the regular layout: its screen hasn't been redesigned and needs page
// scroll + the CC header.
const IN_CALL_PREFIXES = [
  PATH_CALL_CENTER_JOIN.replace(":sessionName", ""),
  PATH_CALL_CENTER_CALL_LEAD.replace(":sessionName", ""),
];

// The call-center header normally bootstraps the call-center list/selection
// (useCallCenters → fetchCallCenters + selectionReady). It is hidden on the
// immersive in-call layout, so this headless mount keeps that bootstrap alive
// — without it, refreshing mid-call leaves selectionReady=false and the whole
// lead pipeline (joinedLeads → useLeadInCall) empty.
const CallCenterSelectionBootstrap = () => {
  useCallCenters();
  return null;
};

const MainContent = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const isInCallPage = IN_CALL_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  // In-call screen is immersive (demo-ui session layout): the content
  // column fills the viewport and the stage/sidebar manage their own
  // scrolling — the page itself never scrolls on desktop.
  if (isInCallPage) {
    return (
      <div
        className="w-full relative bg-[#0d1117] bg-cover flex flex-col lg:h-dvh lg:overflow-hidden"
        style={{ backgroundImage: 'url("/backgrounds/bg-ice.png")', backgroundBlendMode: 'overlay' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 via-transparent to-purple-900/20 pointer-events-none" />
        <CallCenterSelectionBootstrap />
        <main className="relative flex-1 min-h-0 p-3">{children}</main>
      </div>
    );
  }

  return (
    <div
      className="w-full relative bg-[#0d1117] bg-cover overflow-x-hidden"
      style={{ backgroundImage: 'url("/backgrounds/bg-ice.png")', backgroundBlendMode: 'overlay' }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 via-transparent to-purple-900/20 pointer-events-none" />

      <MainTopHeader />
      <main className="p-4">{children}</main>
    </div>
  );
};

const DashboardLayout = ({ children }: Props) => {
  const defaultOpen = Boolean(getCookie(SIDEBAR_COOKIE_NAME));

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <AppSidebar />
      <MainContent>{children}</MainContent>
    </SidebarProvider>
  );
};

export default DashboardLayout;
